import * as $ from 'jquery';
import { BrowserSupport } from '@tableau/browser-support';
import { EventHandleSpec, NormalizedEvent } from '../EventUtils';
import { Gesture, GestureEventInfo } from './Gesture';
import { GestureType, PseudoEventName } from '../../gen-src/preslayer/Enums';
import { GlobalUIEvents } from '../GlobalUIEvents';
import { IMouseCaptureEventHandler } from '../definition/IMouseCaptureEventHandler';
import { Logger, TsConfig } from '../utility/VizclientUtils';
import { MiscUtil } from '../utility/MiscUtil';
import { MouseCapture } from '../MouseCapture';

/**
* Drag gesture implementation. Inherits Gesture
*/
class Drag extends Gesture {

  private readonly mouseCaptureHandler: IMouseCaptureEventHandler;
  private canceled: boolean = false; // has the gesture been canceled?
  private dragging: boolean = false;
  private startInfo: GestureEventInfo; // info on first touch
  private lastInfo: GestureEventInfo; // info on most recent touch

  /**
  * Initializes a new instance of the Drag class
  * @param {GestureEventHandleSpec} config - object
  */
  public constructor(config: EventHandleSpec, mouseCaptureHandler: IMouseCaptureEventHandler) {
    super(config, GestureType.Drag, /* hasSlop */ true);
    this.mouseCaptureHandler = mouseCaptureHandler;
  }

  public Start(e: NormalizedEvent): void {
    Logger.Debug('start');
    let touch: Touch = e.touches[0];

    // Bug 123836, 121420: Don't allow default behavior of copying images when dragging an image
    // Bug 152286: Touch scrolling on vizzes broken in mobile
    if (!MiscUtil.IsNullOrUndefined(e.target) && e.target.tagName.toUpperCase() === 'IMG' && !TsConfig.IsMobile()) {
      e.preventDefault();
    }

    // right number of touches (i.e., fingers)?
    if (e.touches.length !== this.numTouches) {
      // Nope. Cancel anything in progress
      if (this.dragging) {
        this.Fire(PseudoEventName.DragEnd, e, this.lastInfo);
      }
      this.ResetGestureState();
      this.canceled = true;
      return;
    }

    if (!MiscUtil.IsNullOrUndefined(this.startInfo) && !BrowserSupport.IsTouch()) {
      // We're getting multiple starts in a row. Must be we got a mousedown and no mouseup.
      // I bet you $100 we're using Chrome and you clicked on a scroll bar.
      // For touch we don't do this as multitouch can trigger this easily.
      // http://code.google.com/p/chromium/issues/detail?id=14204
      Logger.Debug('multiple starts in a row, ending old one first');
      this.End(e);
    }
    this.canceled = false;
    this.startInfo = this.BuildStartInfo(touch);
    this.lastInfo = this.startInfo;
    this.isPotentialPress = false;
    if (this.Config.IsHandling(PseudoEventName.PotentialPress)) {
      this.StartPotentialPressDetection();
    }
  }

  public Move(e: NormalizedEvent): void {
    if (this.canceled) {
      return;
    }

    // Bug 581838: In order to prevent scrolling and selecting at the same time, track when the
    // user is triggering a potential press and call preventDefault() on any subsequent event.
    // If the user is not scrolling, this will prevent them from starting.
    if (this.isPotentialPress && e.touches.length === this.numTouches) {
      e.preventDefault();
    }

    this.lastInfo = this.GetParams(e);

    if (this.dragging) {
      this.Fire(PseudoEventName.DragMove, e, this.lastInfo);
    } else if (this.PastSlop(e)) {
      this.dragging = true;
      this.Fire(PseudoEventName.DragStart, e, this.lastInfo);
      this.Fire(PseudoEventName.DragMove, e, this.lastInfo);

      // if this config also handles dragEnd, capture events while the mouse is down so we can detect
      // mouseup/mousemove outside the document
      if (!MiscUtil.IsNullOrUndefined(this.Config.dragEnd)) {
        MouseCapture.SetCapture(this.mouseCaptureHandler);
      }
    }
  }


  public End(e: NormalizedEvent): void {
    let param: GestureEventInfo;

    if (!this.dragging) {
      let shouldBeginRecognizingDrag =
        e.touches.length === this.numTouches &&     // Only fire DragStart if we have the correct number of touches
        (!MiscUtil.IsNullOrUndefined(e.touches[0].identifier)) && // Avoid false-positives when handling right clicks
        this.PastSlop(e);                          // Only fire DragStart if we have moved past the allowed slop.

      if (!shouldBeginRecognizingDrag) {
        // TFSID: 624101 - Make sure we reset the gesture state when no drag occurred, otherwise
        // a tap or click will leave us in a bad state when we begin recognizing the next gestures.
        this.ResetGestureState();
        return;
      }

      this.startInfo = this.BuildStartInfo(e.touches[0]);
      this.lastInfo = this.startInfo;
      param = this.GetParams(e);
      this.canceled = false;
      this.dragging = true;

      this.Fire(PseudoEventName.DragStart, e, param);
      return;
    }

    Logger.Debug('end');

    // stop capturing mouse events since we're done dragging
    MouseCapture.ReleaseCapture();

    param = this.GetParams(e);
    this.ResetGestureState();

    // fire global drag end event before drag drop manager performs the drag end
    if (this.Config.IsHandling(PseudoEventName.DragEnd)) {
      // make a new temp event to protect the original, and add in my lastInfo because it
      // holds important gesture info that is missing from OriginalEvent on iPad.
      let tempEvt = NormalizedEvent.Create(e.originalEvent, null);
      $.extend(tempEvt, this.lastInfo);

      GlobalUIEvents.FireDragEnd(tempEvt, null /*DragEnd is not cancellable*/);
    }

    this.Fire(PseudoEventName.DragEnd, e, param);
  }

  public Cancel(): void {
    this.canceled = true;
  }

  /**
  * Given a touch object representing the beginning of the drag gesture, returns a GestureEventInfo that captures
  * the necessary information about the gesture's starting location.
  */
  private BuildStartInfo(touch: Touch): GestureEventInfo {
    let param = new GestureEventInfo();
    param.pageX = touch.pageX;
    param.pageY = touch.pageY;
    return param;
  }

  /**
  * Given a "move" event, returns additional information that gives context about the event relative to the entire
  * drag gesture.
  */
  private GetParams(evt: NormalizedEvent): GestureEventInfo {
    let touch: Touch = evt.changedTouches[0];
    if (MiscUtil.IsNullOrUndefined(this.startInfo)) {
      this.startInfo = this.BuildStartInfo(touch);
    }
    if (MiscUtil.IsNullOrUndefined(this.lastInfo)) {
      this.lastInfo = this.startInfo;
    }

    let param = new GestureEventInfo();
    param.clientX = touch.clientX;
    param.clientY = touch.clientY;
    param.pageX = touch.pageX;
    param.pageY = touch.pageY;
    param.startX = this.startInfo.pageX;
    param.startY = this.startInfo.pageY;
    param.deltaX = param.pageX - this.startInfo.pageX;
    param.deltaY = param.pageY - this.startInfo.pageY;
    param.deltaXSinceLast = param.pageX - this.lastInfo.pageX;
    param.deltaYSinceLast = param.pageY - this.lastInfo.pageY;
    param.scale = 1;
    return param;
  }

  private PastSlop(evt: NormalizedEvent): boolean {
    let touch: Touch = evt.touches[0];
    if (MiscUtil.IsNullOrUndefined(this.startInfo)) {
      return false; // we haven't even started yet
    }

    let zoomAdjustedSlop: number = this.ZoomAdjustedSlop;
    return Math.abs(touch.pageX - this.startInfo.pageX) > zoomAdjustedSlop ||
      Math.abs(touch.pageY - this.startInfo.pageY) > zoomAdjustedSlop;
  }

  /**
   * Reset the gesture state so that no gesture is in the process of being recognized.
   */
  private ResetGestureState(): void {
    this.startInfo = null;
    this.dragging = false;
    this.isPotentialPress = false;
    this.potentialPressTimer = MiscUtil.ClearTimeout(this.potentialPressTimer);
  }

  private StartPotentialPressDetection(): void {
    this.potentialPressTimer = window.setTimeout(() => {
      // TFSID 625762 - Fail the potential press detection if we have already started dragging by the time
      // this callback is invoked.
      // This stops us from accidentally killing some native browser interactions. As of 2017-03-29, I am
      // aware of two specific situations situations where this is relevant:
      // (1) scrolling via dragging on a touch-enabled device
      // (2) dragging an HTML5 slider
      if (!this.dragging) {
        this.isPotentialPress = true;
      }
      // tslint:disable-next-line:align
    }, Gesture.PotentialPressTime);
  }
}

export { Drag };
